import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiImage, FiSearch, FiSend } from "react-icons/fi";
import api from "../services/api";
import { useWebSocket } from "../context/WebSocketContext";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const Messages = () => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [threadError, setThreadError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesRef = useRef(null);
  const { isConnected, addMessageListener, sendMessage } = useWebSocket();
  const selectedThreadIdRef = useRef(0);
  const messagesStateRef = useRef([]);

  const scopeValue = localStorage.getItem("rmc2.scope") || "";
  const scopes = scopeValue
    .split(" ")
    .map((scope) => scope.trim())
    .filter(Boolean);
  const isAdmin = scopes.includes("admin");
  useEffect(() => {
    selectedThreadIdRef.current = Number(selectedThread?.thread_id || 0);
  }, [selectedThread]);

  useEffect(() => {
    messagesStateRef.current = messages;
  }, [messages]);

  const totalPages = useMemo(() => {
    return lastPage || 1;
  }, [lastPage]);

  const markSeen = useCallback(async (threadIdOverride) => {
    const token = localStorage.getItem("rmc2.access_token");
    if (!token) {
      return;
    }
    try {
      if (isAdmin) {
        if (!threadIdOverride) {
          return;
        }
        await api.post(
          `/messages/threads/${threadIdOverride}/seen`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        await api.post(
          "/messages/thread/seen",
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      if (threadIdOverride) {
        sendMessage({
          type: "chat_seen",
          token,
          thread_id: threadIdOverride,
        });
      }
    } catch (error) {
      return;
    }
  }, [isAdmin, sendMessage]);

  const handleSeenUpdate = useCallback((payload) => {
    if (!isAdmin) {
      return;
    }
    if (payload.viewer_role !== "admin") {
      return;
    }
    setThreads((prev) =>
      prev.map((thread) =>
        thread.thread_id === payload.thread_id
          ? { ...thread, unread_count: 0 }
          : thread
      )
    );
  }, [isAdmin]);

  const handleIncomingMessage = useCallback((payload) => {
    const incoming = payload.message;
    if (!incoming) {
      return;
    }

    let incomingThreadId = Number(payload.thread_id || incoming.thread_id || 0);
    const selectedThreadId = selectedThreadIdRef.current;
    if (isAdmin && selectedThreadId && !incomingThreadId) {
      incomingThreadId = selectedThreadId;
    }

    if (!isAdmin) {
      if (!selectedThread || selectedThreadId !== incomingThreadId) {
        setSelectedThread((prev) => ({
          ...(prev || {}),
          thread_id: incomingThreadId,
        }));
      }
    }

    if (incoming.client_temp_id) {
      const hasTemp = messagesStateRef.current.some(
        (item) => item.client_temp_id === incoming.client_temp_id
      );
      if (hasTemp) {
        return;
      }
    }

    if (isAdmin) {
      setThreads((prev) => {
        const next = [...prev];
        const idx = next.findIndex(
          (thread) => Number(thread.thread_id || 0) === incomingThreadId
        );
        if (idx >= 0) {
          const current = next[idx];
          const unread =
            selectedThreadId === incomingThreadId
              ? 0
              : Number(current.unread_count || 0) + (incoming.sender_role === "member" ? 1 : 0);
          next[idx] = {
            ...current,
            last_message_at: incoming.created_at,
            last_message_preview: incoming.message || "Attachment",
            last_message_sender_role: incoming.sender_role,
            unread_count: unread,
          };
        }
        return next;
      });
    }

    if (isAdmin && (!selectedThreadId || selectedThreadId !== incomingThreadId)) {
      return;
    }

    setMessages((prev) => mergeMessages(prev, [incoming]));
    setTimeout(() => {
      if (messagesRef.current) {
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      }
    }, 0);
    if (!isAdmin) {
      markSeen();
    }
  }, [isAdmin, markSeen, messages, selectedThread]);

  useEffect(() => {
    const unsubscribe = addMessageListener((payload) => {
      if (payload.type === "chat_message_new") {
        handleIncomingMessage(payload);
      }
      if (payload.type === "chat_seen_update") {
        handleSeenUpdate(payload);
      }
    });
    return unsubscribe;
  }, [addMessageListener, handleIncomingMessage, handleSeenUpdate]);

  useEffect(() => {
    if (isAdmin) {
      fetchThreads();
    } else {
      fetchMemberThread();
    }
  }, [query, page, perPage, unreadOnly]);

  const fetchThreads = async () => {
    setIsLoadingThreads(true);
    setThreadError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setThreadError("Missing access token.");
        return;
      }
      const response = await api.get("/messages/threads", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: query,
          page,
          per_page: perPage,
          unread_only: unreadOnly ? 1 : 0,
        },
      });
      if (response.data?.status !== "success") {
        setThreadError("Failed to load threads.");
        return;
      }
      const data = response.data?.data || {};
      setThreads(data.rows || []);
      setLastPage(data.last_page || 1);
    } catch (error) {
      setThreadError("Failed to load threads.");
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const fetchMemberThread = async () => {
    setIsLoadingThreads(true);
    setThreadError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setThreadError("Missing access token.");
        return;
      }
      const response = await api.get("/messages/thread", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.status !== "success") {
        setThreadError("Failed to load messages.");
        return;
      }
      const thread = response.data?.data?.thread || null;
      setSelectedThread({ thread_id: thread?.id || null });
      if (thread?.id) {
        loadMessages(thread.id);
      }
    } catch (error) {
      setThreadError("Failed to load messages.");
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const loadMessages = async (threadId, beforeId) => {
    if (!threadId) {
      return;
    }
    setIsLoadingMessages(!beforeId);
    setIsLoadingMore(!!beforeId);
    setMessageError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setMessageError("Missing access token.");
        return;
      }
      const url = isAdmin
        ? `/messages/threads/${threadId}/messages`
        : "/messages/thread/messages";
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 20,
          before_id: beforeId || undefined,
        },
      });
      if (response.data?.status !== "success") {
        setMessageError("Failed to load messages.");
        return;
      }
      const data = response.data?.data || {};
      if (beforeId) {
        setMessages((prev) => [...data.rows, ...prev]);
      } else {
        setMessages((prev) => {
          const merged = mergeMessages(prev, data.rows || []);
          return merged;
        });
      }
      setHasMore(!!data.has_more);
      if (!beforeId) {
        setTimeout(() => {
          if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
          }
        }, 0);
      }
      markSeen(threadId);
    } catch (error) {
      setMessageError("Failed to load messages.");
    } finally {
      setIsLoadingMessages(false);
      setIsLoadingMore(false);
    }
  };

  const handleSelectThread = async (thread) => {
    let threadId = thread.thread_id;
    if (!threadId) {
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          return;
        }
        const response = await api.post(
          "/messages/threads/start",
          { oauth_user_id: thread.oauth_user_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        threadId = response.data?.data?.thread_id || null;
      } catch (error) {
        return;
      }
    }

    setSelectedThread({ ...thread, thread_id: threadId });
    setMessages([]);
    await loadMessages(threadId);
    setThreads((prev) =>
      prev.map((item) =>
        item.oauth_user_id === thread.oauth_user_id
          ? { ...item, thread_id: threadId, unread_count: 0 }
          : item
      )
    );
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSend = async () => {
    if (!inputValue.trim() && !selectedFile) {
      return;
    }
    const token = localStorage.getItem("rmc2.access_token");
    if (!token) {
      return;
    }

    setIsSending(true);
    try {
      const clientTempId = `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const optimisticAttachmentUrl = selectedFile
        ? await toBase64(selectedFile)
        : null;
      const optimisticMessage = {
        id: clientTempId,
        thread_id: selectedThread?.thread_id || null,
        client_temp_id: clientTempId,
        sender_role: isAdmin ? "admin" : "member",
        sender_name: isAdmin ? "Admin" : "You",
        message: inputValue.trim(),
        attachment_original_name: selectedFile?.name || null,
        attachment_url: optimisticAttachmentUrl,
        attachment_is_temporary: false,
        created_at: new Date().toISOString(),
      };

      if (selectedThread?.thread_id) {
        setMessages((prev) => [...prev, optimisticMessage]);
        setTimeout(() => {
          if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
          }
        }, 0);
      }

      if (isAdmin && selectedThread?.oauth_user_id) {
        setThreads((prev) =>
          prev.map((thread) =>
            thread.oauth_user_id === selectedThread.oauth_user_id
              ? {
                  ...thread,
                  last_message_at: new Date().toISOString(),
                  last_message_preview: inputValue.trim() || "Attachment",
                  last_message_sender_role: "admin",
                }
              : thread
          )
        );
      }

      let attachmentPayload = null;
      if (selectedFile) {
        const dataBase64 = optimisticAttachmentUrl || (await toBase64(selectedFile));
        attachmentPayload = {
          file_name: selectedFile.name,
          content_type: selectedFile.type,
          data_base64: dataBase64,
        };
      }

      sendMessage({
        type: "chat_message_send",
        token,
        thread_id: selectedThread?.thread_id || null,
        message: inputValue.trim(),
        attachment: attachmentPayload,
        client_temp_id: clientTempId,
      });
      setInputValue("");
      setSelectedFile(null);
      setFileName("");
    } finally {
      setIsSending(false);
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  const mergeMessages = (current, next) => {
    const map = new Map();
    [...current, ...next].forEach((item) => {
      if (!item) {
        return;
      }
      const key = item.client_temp_id ? `tmp:${item.client_temp_id}` : `id:${item.id}`;
      if (!map.has(key)) {
        map.set(key, item);
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return aTime - bTime;
    });
  };

  const handleScroll = () => {
    if (!messagesRef.current || !hasMore || isLoadingMore) {
      return;
    }
    if (messagesRef.current.scrollTop <= 120) {
      const oldest = messages[0];
      if (oldest?.id) {
        loadMessages(selectedThread?.thread_id, oldest.id);
      }
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }
    if (event.shiftKey) {
      return;
    }
    event.preventDefault();
    handleSend();
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Messages
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">
          {isAdmin ? "Support Inbox" : "Chat Support"}
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {isAdmin
            ? "Respond to member inquiries and manage message threads."
            : "Send your concerns and receive responses from support."}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.4fr]">
        {isAdmin && (
          <div className="rounded-3xl border border-white/60 bg-white/90 p-5 shadow-lg shadow-slate-200/60">
            <form onSubmit={handleSearchSubmit} className="relative">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search members"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </form>

            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={unreadOnly}
                  onChange={(event) => {
                    setUnreadOnly(event.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-[var(--brand-orange)] focus:ring-orange-200"
                />
                Unread only
              </label>
              <div className="flex items-center gap-2">
                <span>Display</span>
                <select
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-xs text-slate-600"
                >
                  {PER_PAGE_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {threadError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs text-rose-600">
                {threadError}
              </div>
            )}

            <div className="mt-4 space-y-2">
              {isLoadingThreads ? (
                <p className="text-sm text-slate-400">Loading members...</p>
              ) : threads.length === 0 ? (
                <p className="text-sm text-slate-400">No members found.</p>
              ) : (
                threads.map((thread) => (
                  <button
                    key={thread.oauth_user_id}
                    type="button"
                    onClick={() => handleSelectThread(thread)}
                    className={`flex w-full items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                      selectedThread?.oauth_user_id === thread.oauth_user_id
                        ? "border-[var(--brand-blue)] bg-blue-50/40"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        {[thread.first_name, thread.last_name].filter(Boolean).join(" ") || "Member"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {thread.referral_code || "No referral code"}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {thread.last_message_preview || "No messages yet"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 text-xs text-slate-400">
                      <span>{thread.last_message_at ? "Active" : "New"}</span>
                      {Number(thread.unread_count || 0) > 0 && (
                        <span className="rounded-full bg-[var(--brand-orange)] px-2 py-0.5 text-[10px] font-semibold text-white">
                          {thread.unread_count}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="rounded-xl border border-slate-200 px-3 py-2 disabled:opacity-50"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="rounded-xl border border-slate-200 px-3 py-2 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="rounded-3xl border border-white/60 bg-white/90 p-5 shadow-lg shadow-slate-200/60">
          {messageError && (
            <div className="mb-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs text-rose-600">
              {messageError}
            </div>
          )}

          {isAdmin && !selectedThread?.thread_id ? (
            <div className="flex h-[420px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
              Select a member to start messaging.
            </div>
          ) : (
            <div
              ref={messagesRef}
              onScroll={handleScroll}
              className="h-[420px] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4"
            >
              {isLoadingMessages ? (
                <p className="text-sm text-slate-400">Loading messages...</p>
              ) : messages.length === 0 ? (
                <p className="text-sm text-slate-400">No messages yet.</p>
              ) : (
                messages.map((message) => {
                  const isOwn = isAdmin
                    ? message.sender_role === "admin"
                    : message.sender_role === "member";
                  return (
                    <div
                      key={message.id}
                      className={`mb-4 flex ${isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                          isOwn
                            ? "bg-[var(--brand-orange)] text-white"
                            : "bg-white text-slate-700"
                        }`}
                      >
                        <div className="text-xs font-semibold text-slate-200">
                          {!isOwn && message.sender_name ? message.sender_name : ""}
                        </div>
                        {message.message && <p>{message.message}</p>}
                        {message.attachment_url && (
                          <button
                            type="button"
                            onClick={() => window.open(message.attachment_url, "_blank")}
                            className={`mt-2 block w-full rounded-xl border px-3 py-2 text-xs font-semibold ${
                              isOwn
                                ? "border-white/40 text-white"
                                : "border-slate-200 text-slate-600"
                            }`}
                          >
                            View Attachment
                          </button>
                        )}
                        <p className="mt-2 text-[10px] text-slate-200">
                          {message.created_at}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600">
              <FiImage />
              <span>{fileName || "Attach image"}</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  setSelectedFile(file || null);
                  setFileName(file?.name || "");
                }}
                disabled={isAdmin && !selectedThread?.thread_id}
              />
            </label>
            <textarea
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Type your message..."
              rows={1}
              className="flex-1 resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
              disabled={isAdmin && !selectedThread?.thread_id}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={
                isSending ||
                (!inputValue.trim() && !selectedFile) ||
                (isAdmin && !selectedThread?.thread_id)
              }
              className="inline-flex items-center gap-2 rounded-2xl bg-[var(--brand-orange)] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:opacity-60"
            >
              <FiSend />
              Send
            </button>
          </div>
          {!isConnected && (
            <p className="mt-3 text-xs text-rose-500">
              WebSocket disconnected. Messages will sync once reconnected.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
